import type { I18nContent } from '@/types/api';

export type { I18nContent };

/** Supported languages */
const SUPPORTED_LANGUAGES = ['zh', 'en', 'ms'] as const;
const DEFAULT_LANGUAGE = 'en';
const LANG_STORAGE_KEY = 'goloka_lang';

/**
 * Extract the display text for the current language from a multi-language
 * JSON content object.
 *
 * Fallback order: requested language → English → first available key → fallback string.
 */
export function getLocalizedText(
  content: I18nContent | null | undefined,
  lang: string,
  fallback: string = ''
): string {
  if (!content || typeof content !== 'object') return fallback;
  if (content[lang]) return content[lang];
  if (content['en']) return content['en'];
  const firstKey = Object.keys(content)[0];
  return firstKey ? (content[firstKey] ?? fallback) : fallback;
}

/**
 * Resolve the initial UI language using the following priority order:
 *
 * 1. Authenticated user's profile `language_preference`.
 * 2. Cached value in localStorage (`goloka_lang`).
 * 3. First supported language found in `navigator.languages` (the full
 *    browser preference list). Falls back to `[navigator.language]` for
 *    browsers that don't expose the array.
 * 4. Hardcoded default `'en'`.
 *
 * Using `navigator.languages` (plural) instead of `navigator.language`
 * (singular) means the browser's entire ranked preference list is checked,
 * so a user whose list is ["zh-TW", "en-US"] correctly gets "zh", while a
 * user with ["fr", "en-GB"] correctly gets "en" rather than falling through
 * to the hardcoded default.
 */
export function resolveInitialLang(userLang?: string | null): string {
  const supported: readonly string[] = SUPPORTED_LANGUAGES;

  if (userLang && supported.includes(userLang)) return userLang;

  const cached = localStorage.getItem(LANG_STORAGE_KEY);
  if (cached && supported.includes(cached)) return cached;

  // Walk the full browser language preference list so that e.g. ["zh-TW", "en-US"]
  // correctly resolves to "zh", and ["fr", "en-GB"] resolves to "en" rather than
  // incorrectly falling back to the hardcoded default.
  // Guard navigator access so this function is safe in SSR / non-browser environments.
  const browserLangs: readonly string[] =
    typeof navigator !== 'undefined'
      ? (navigator.languages?.length ? navigator.languages : [navigator.language])
      : [];

  for (const lang of browserLangs) {
    if (typeof lang !== 'string' || !lang) continue;
    const base = (lang.split('-')[0] ?? '').toLowerCase();
    if (supported.includes(base)) return base;
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Asynchronously load all translation files for the given language.
 *
 * Fetches each module JSON from `/locales/{lang}/{module}.json` in parallel
 * and merges the results into a single flat key→value map. Any individual
 * module that fails to load is silently skipped so a single missing file
 * does not break the entire translation set.
 */
export async function loadTranslations(lang: string): Promise<Record<string, string>> {
  const modules = ['common', 'auth', 'package', 'order', 'wallet', 'points', 'admin'];
  const results = await Promise.all(
    modules.map(async (mod) => {
      try {
        const resp = await fetch(`/locales/${lang}/${mod}.json`);
        if (!resp.ok) return {};
        return (await resp.json()) as Record<string, string>;
      } catch {
        return {};
      }
    })
  );
  return Object.assign({}, ...results) as Record<string, string>;
}
